import { IonRouterLink } from '@ionic/react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ExpandableSection from '@components/animations/ExpandableSection';
import InitialConsultFinancialForm from '@components/forms/InitialConsultFinancialForm';
import PersonalizeForm from '@components/forms/PersonalizeForm';
import InitialConsultQuestion from '@components/InitialConsultQuestion';
import Modal from '@components/Modal';
import PMEQuestion from '@components/PMEQuestion';
import styles from '@components/profile/Profile.module.css';
import Icon from '@components/text/Icon';
import Heading2 from '@components/typography/Heading2';
import useOpener from '@hooks/useOpener';
import {
  CareProfileActionableFieldEnum,
  CareProfileHealthInsuranceTypeEnum,
  CareProfileInsuranceTypeEnum,
  GetJourneyWithCareProfileQuery
} from '@typing/Generated';
import { InitialConsultFields, PersonalizeExperienceFields } from '@typing/Models';
type Props = {
  journey: GetJourneyWithCareProfileQuery['journey'];
};

const Financial = ({ journey }: Props) => {
  const opener = useOpener();
  const intl = useIntl();

  const [editingQuestion, setEditingQuestion] = useState<PersonalizeExperienceFields[]>([]);
  const [editingInitialConsult, setEditingInitialConsult] = useState<InitialConsultFields[] | undefined>(undefined);

  const personalizeFinancialOpener = useOpener();
  const initialConsultOpener = useOpener();

  const handlePMEQuestionChange = useCallback(
    (field: PersonalizeExperienceFields) => () => {
      setEditingQuestion([field]);
      personalizeFinancialOpener.open();
    },
    [personalizeFinancialOpener]
  );

  const handleInitialConsultQuestionChange = useCallback(
    (field: InitialConsultFields) => () => {
      setEditingInitialConsult([field]);
      initialConsultOpener.open();
    },
    [initialConsultOpener]
  );

  const providerFinancialConcerns = useMemo(() => {
    const careTransitions = journey.careProfile.providerFinancialConcerns?.providerFinancialConcernsOptions.map(
      option =>
        intl.formatMessage({
          id: `models.pme.questions.provider_financial_concerns.options.${option}.content`
        })
    );

    if (journey.careProfile.providerFinancialConcerns?.other) {
      careTransitions?.push(journey.careProfile.providerFinancialConcerns?.other);
    }
    return careTransitions ?? [];
  }, [
    journey.careProfile.providerFinancialConcerns?.providerFinancialConcernsOptions,
    journey.careProfile.providerFinancialConcerns?.other,
    intl
  ]);
  return (
    <div>
      <IonRouterLink onClick={opener.toggle}>
        <div className={styles.section}>
          <Heading2>
            <FormattedMessage id="pages.client.careProfile.sections.financial" />
          </Heading2>
          <Icon
            ariaLabelKey="accessibility.ariaLabels.toggle"
            className={styles.toggleOpener}
            icon={opener.isOpen ? 'chevronUp' : 'chevronDown'}
          />
        </div>
      </IonRouterLink>
      <ExpandableSection isOpen={opener.isOpen}>
        <PMEQuestion
          field={CareProfileActionableFieldEnum.provider_financial_concerns}
          handleEdit={handlePMEQuestionChange}
          selections={providerFinancialConcerns}
        />
        <PMEQuestion
          field={CareProfileActionableFieldEnum.provider_finances_impacted}
          handleEdit={handlePMEQuestionChange}
          selections={[
            journey.careProfile.providerFinancesImpacted === true
              ? intl.formatMessage({ id: 'dictionary.yes' })
              : intl.formatMessage({ id: 'dictionary.no' })
          ]}
        />

        <InitialConsultQuestion
          field={InitialConsultFields.VA_CONNECTED}
          handleEdit={handleInitialConsultQuestionChange}
          selections={[
            journey.careProfile.vaConnected
              ? intl.formatMessage({ id: 'dictionary.yes' })
              : journey.careProfile.vaConnected === false
              ? intl.formatMessage({ id: 'dictionary.no' })
              : null
          ]}
          titleKey="models.initialConsultReport.questions.va_connected.content"
        />
        {journey.careProfile.vaConnected && (
          <InitialConsultQuestion
            field={InitialConsultFields.VETERAN_CONNECTION}
            handleEdit={handleInitialConsultQuestionChange}
            selections={
              journey.careProfile.veteranConnection?.map(connection =>
                intl.formatMessage({
                  id: `models.initialConsultReport.questions.veteran_connection.options.${connection}.content`
                })
              ) ?? []
            }
            titleKey="models.initialConsultReport.questions.veteran_connection.content"
          />
        )}
        <InitialConsultQuestion
          field={InitialConsultFields.HEALTH_INSURANCE_TYPES}
          handleEdit={handleInitialConsultQuestionChange}
          selections={
            journey.careProfile.healthInsuranceTypes?.healthInsuranceTypesOptions?.map(option => {
              if (
                option === CareProfileHealthInsuranceTypeEnum.other &&
                journey.careProfile.healthInsuranceTypes?.other
              ) {
                return journey.careProfile.healthInsuranceTypes?.other;
              }
              return intl.formatMessage({
                id: `models.initialConsultReport.questions.health_insurance_types.options.${option}.content`
              });
            }) ?? []
          }
          titleKey="models.initialConsultReport.questions.health_insurance_types.content"
        />
        <InitialConsultQuestion
          field={InitialConsultFields.FINANCIAL_MANAGER}
          handleEdit={handleInitialConsultQuestionChange}
          selections={[
            journey.careProfile.financialManager?.other
              ? journey.careProfile.financialManager.other
              : journey.careProfile.financialManager?.financialManagerOption
              ? intl.formatMessage({
                  id: `models.initialConsultReport.questions.financial_manager.options.${journey.careProfile.financialManager.financialManagerOption}.content`
                })
              : null
          ]}
          titleKey="models.initialConsultReport.questions.financial_manager.content"
        />
        <InitialConsultQuestion
          field={InitialConsultFields.INSURANCE_TYPES}
          handleEdit={handleInitialConsultQuestionChange}
          selections={
            journey.careProfile.insuranceTypes?.insuranceTypesOptions?.map(option => {
              if (option === CareProfileInsuranceTypeEnum.other && journey.careProfile.insuranceTypes?.other) {
                return journey.careProfile.insuranceTypes.other;
              }
              return intl.formatMessage({
                id: `models.initialConsultReport.questions.insurance_types.options.${option}.content`
              });
            }) ?? []
          }
          titleKey="models.initialConsultReport.questions.insurance_types.content"
        />

        <Modal
          opener={personalizeFinancialOpener}
          title={intl.formatMessage({ id: 'models.careProfile.editCareProfile' })}
        >
          {() => (
            <PersonalizeForm
              careProfile={journey.careProfile}
              clientRole={null}
              onCancel={personalizeFinancialOpener.close}
              onSuccess={personalizeFinancialOpener.close}
              questions={editingQuestion}
            />
          )}
        </Modal>
        <Modal opener={initialConsultOpener} title={intl.formatMessage({ id: 'models.careProfile.editCareProfile' })}>
          {() => (
            <InitialConsultFinancialForm
              careProfile={journey.careProfile}
              consultId={journey.initialConsultReport.consult?.id ?? null}
              initialConsultReportId={journey.initialConsultReport.id}
              onCancel={initialConsultOpener.close}
              onSuccess={initialConsultOpener.close}
              questions={editingInitialConsult}
            />
          )}
        </Modal>
      </ExpandableSection>
    </div>
  );
};

export default Financial;
